import { useQuery } from '@tanstack/react-query';
import { listActiveBannerAds } from '@/api/bannerAds';
import { getVendor } from '@/api/catalog';
import { bannerAdToAdUnit } from '@/api/promoAds';
import { AdBanner, type AdBannerProps } from '@/components/ads/AdBanner';

const PLACEMENT = 'homepage_footer' as const;

function pickRandom<T>(items: T[]): T | null {
  if (items.length === 0) return null;
  return items[Math.floor(Math.random() * items.length)] ?? null;
}

async function loadFooterAdProps(): Promise<AdBannerProps | null> {
  const ads = await listActiveBannerAds([PLACEMENT]);
  const banner = pickRandom(ads);
  if (!banner) return null;

  const vendor = banner.vendorId ? await getVendor(banner.vendorId) : undefined;
  const unit = bannerAdToAdUnit(banner, 'banner', vendor);

  return {
    adId: unit.id,
    advertiserName: unit.advertiserName,
    advertiserInitials: unit.advertiserInitials,
    logoBackground: unit.logoBackground,
    logoColor: unit.logoColor,
    headline: unit.headline,
    subline: unit.subline,
    ctaLabel: unit.ctaLabel,
    ctaHref: unit.ctaHref,
    variant: unit.variant === 'gold' ? 'gold' : 'default',
  };
}

/** Admin-managed promo below “Why ShopGND” — random active `homepage_footer` creative. */
export function HomepageFooterAd() {
  const { data: ad, isLoading } = useQuery({
    queryKey: ['ads', PLACEMENT],
    queryFn: loadFooterAdProps,
    staleTime: 3 * 60_000,
  });

  if (isLoading || !ad) return null;

  return (
    <section className="mx-auto max-w-[1400px] px-4 pb-10 sm:px-6">
      <AdBanner {...ad} className="rounded-2xl shadow-sm" />
    </section>
  );
}
